export type SiteChatLang = 'en' | 'ru' | 'uk' | 'es' | 'id' | 'lt' | 'pt' | 'zhcn' | 'zhtw';

export type SiteChatColors = {
    button: string;
    header: string;
    guestBubble: string;
    guestText: string;
    operatorBubble: string;
    operatorText: string;
};

export type SiteChatTeamMember = {
    name: string;
    /** Public URL of the photo (an AccelFile in the school's public images). */
    photo?: string | null;
};

export type SiteChatFieldType = 'text' | 'email' | 'phone';

export type SiteChatField = {
    name: string;
    type: SiteChatFieldType;
    required: boolean;
    enabled: boolean;
    /** Name, email and phone stay in the list. They can be turned off, not deleted. */
    builtin: boolean;
    placeholder: string;
};

export type SiteChatFormPhrases = {
    title: string;
    button: string;
    empty: string;
    email: string;
    phone: string;
    network: string;
};

export type SiteChatDay = {
    on: boolean;
    from: string;
    to: string;
};

export type SiteChatUrlOp = 'contains' | 'equals' | 'not_contains' | 'not_equals';

export type SiteChatRule = {
    op: SiteChatUrlOp;
    value: string;
};

export type SiteChatTrigger = {
    name: string;
    conditions: SiteChatRule[];
    seconds: number;
    when: 'work' | 'off' | 'always';
    action: 'phrase' | 'hide';
    text: string;
    enabled: boolean;
};

/** `system` follows the visitor's OS setting (`prefers-color-scheme`). */
export type SiteChatTheme = 'light' | 'dark' | 'system';

export type SiteChatConfig = {
    colors: SiteChatColors;
    theme: SiteChatTheme;
    /** The emoji button in the composer. */
    emoji: boolean;
    /** Voice messages: the microphone button in the composer. */
    voice: boolean;
    /** Marks under the visitor's messages: delivered (one tick) and read by the school's side (two ticks). Off by default. */
    readReceipts: boolean;
    /** Longest visitor message, characters (the server caps it at 10 000). */
    maxLength: number;
    /** Largest attachment, MB (the server caps it at 20). */
    maxFileSizeMb: number;
    /** Extensions a visitor may attach, lowercase without the dot; empty = any that is not executable. */
    allowedExtensions: string[];
    position: 'left' | 'right';
    offsetX: number;
    offsetY: number;
    width: number;
    lang: SiteChatLang;
    logo: string | null;
    title: string;
    subtitleOnline: string;
    subtitleOffline: string;
    greeting: string;
    cardText: string;
    policyUrl: string;
    showTeam: boolean;
    team: SiteChatTeamMember[];
    showReplyTime: boolean;
    replyOnline: string;
    replyOffline: string;
    /** The lead form at all: off = never shown and never required, whatever the two «don't accept» switches say. */
    formEnabled: boolean;
    formRequiredOnline: boolean;
    formRequiredOffline: boolean;
    fields: SiteChatField[];
    formPhrases: SiteChatFormPhrases;
    composerPlaceholder: string;
    offlineBanner: string;
    noConnection: string;
    retry: string;
    alwaysOnline: boolean;
    timezone: string;
    days: SiteChatDay[];
    mobileOnly: boolean;
    hideMobile: boolean;
    rules: SiteChatRule[];
    triggers: SiteChatTrigger[];
};

export type SiteChatScreen =
    | 'launcher'
    | 'launcher-hover'
    | 'launcher-unread'
    | 'launcher-unread-max'
    | 'launcher-hint'
    | 'launcher-hint-left'
    | 'launcher-loading'
    | 'home'
    | 'home-one'
    | 'home-no-avatars'
    | 'home-offline'
    | 'form'
    | 'form-error'
    | 'form-sending'
    | 'form-network'
    | 'thread-greeting'
    | 'thread-empty-offline'
    | 'thread'
    | 'thread-files'
    | 'thread-hint'
    | 'thread-offline'
    | 'no-connection'
    | 'composer';

export type SiteChatPreview =
    | 'home'
    | 'off'
    | 'form'
    | 'empty'
    | 'thread'
    | 'hint'
    | 'err'
    /** No button of its own: the «Offline» tab on a thread, for the strip above the message field. */
    | 'offThread';

export const PREVIEW_SCREEN: Record<SiteChatPreview, SiteChatScreen> = {
    home: 'home',
    off: 'home-offline',
    form: 'form',
    empty: 'thread-greeting',
    thread: 'thread',
    hint: 'launcher-hint',
    err: 'no-connection',
    offThread: 'thread-offline',
};

export const SITE_CHAT_LANGS: SiteChatLang[] = ['en', 'ru', 'uk', 'es', 'id', 'lt', 'pt', 'zhcn', 'zhtw'];

/** Who wrote a school-side message, as the visitor may see it. */
export type SiteChatAuthor = {
    name?: string | null;
    avatarUrl?: string | null;
};

export type SiteChatAttachment = {
    id: string;
    name: string;
    extension?: string | null;
    mimeType?: string | null;
    size: number;
    type: 'picture' | 'video' | 'audio' | 'document';
    url: string;
    /** A recorded voice message: drawn as a player, not a file card. */
    voice?: boolean;
    /** Voice only: length in seconds and the waveform (0..1), as the recorder measured them. */
    duration?: number | null;
    levels?: number[] | null;
};

export type SiteChatButton = {
    index: number;
    title: string;
    type: 'default' | 'link' | 'phone';
    link?: string | null;
};

/** A message in the visitor's thread (`SiteChatMessageModel` on the server). */
export type SiteChatMessage = {
    id: string;
    index: number;
    text?: string | null;
    markdown?: boolean;
    fromVisitor: boolean;
    createdDate: string;
    clientId?: string | null;
    author?: SiteChatAuthor | null;
    attachments?: SiteChatAttachment[] | null;
    buttons?: SiteChatButton[] | null;
    /**
     * Client-side only: a visitor message on its way, one that did not leave, or one `held` until the visitor fills
     * in the required lead form (it is not on the server yet).
     */
    status?: 'sending' | 'failed' | 'held';
    /** Client-side only: a greeting or trigger phrase drawn by the widget, never stored. */
    local?: boolean;
};

/** What a file is for drawing it: a picture or a video gets a thumbnail, audio a player, anything else a file card. */
export type SiteChatFileKind = 'picture' | 'video' | 'audio' | 'document';

export type SiteChatUpload = {
    key: string;
    name: string;
    /** Without the dot, lowercase: `pdf`, `png`. */
    extension: string;
    kind: SiteChatFileKind;
    size: number;
    state: 'uploading' | 'ready' | 'error' | 'too-large' | 'type';
    progress: number;
    /** A local object URL of a picture or a video, for the thumbnail while it uploads. */
    previewUrl?: string | null;
    attachment?: SiteChatAttachment;
};

/** The lead form is no screen of its own: it is a card in the thread, under the visitor's first message. */
export type SiteChatView = 'home' | 'thread';

/**
 * `site`: the widget a school pastes on a website — launcher, home screen, lead form.
 * `school`: the membership area's chat in School — the student is signed in, there is one thread and no launcher;
 * the host's own header button opens and closes the panel.
 */
export type SiteChatMode = 'site' | 'school';

/** A finished recording, ready to send. */
export type SiteChatVoice = {
    blob: Blob;
    duration: number;
    levels: number[];
    mimeType: string;
};

export type SiteChatActions = {
    toggle(): void;
    close(): void;
    openThread(): void;
    back(): void;
    setFormValue(index: number, value: string): void;
    submitForm(): void;
    /** An optional form only: the visitor goes on without it, and this browser is not asked again. */
    skipForm(): void;
    setText(text: string): void;
    send(): void;
    resend(message: SiteChatMessage): void;
    attach(files: File[]): void;
    removeUpload(key: string): void;
    retryUpload(key: string): void;
    retry(): void;
    openHint(): void;
    dismissHint(): void;
    clickButton(message: SiteChatMessage, button: SiteChatButton): void;
    loadMore(): void;
    sendVoice(voice: SiteChatVoice): void;
    /** A screenshot of the screen the visitor picks in the browser's dialog; see `canScreenshot`. */
    screenshot?(): void;
};

/**
 * Everything the widget draws. The live widget builds it from the visitor session; the admin preview and
 * the state gallery build it from mocks — both render the same components.
 */
export type SiteChatModel = {
    config: SiteChatConfig;
    mode: SiteChatMode;
    channelName: string;
    online: boolean;
    open: boolean;
    view: SiteChatView;
    /** The session is still starting: the button shows a spinner and does not open. */
    loading: boolean;
    /** The panel is open and the service cannot be reached. */
    connectionError: boolean;
    unread: number;
    hint: string | null;
    messages: SiteChatMessage[];
    typing: SiteChatAuthor | null;
    hasMore: boolean;
    /** The visitor's messages up to this index are read (an operator saw them, or someone replied after them). */
    readIndex?: number;
    /** The browser can capture the screen (desktop): the paperclip offers «Screenshot» next to «File». */
    canScreenshot?: boolean;
    /** A screenshot is being taken: the widget hides itself for that frame. */
    capturing?: boolean;
    form: {
        /** The form card is in the thread, under the visitor's messages. */
        shown: boolean;
        /** Required: no «skip», and the composer waits until it is filled in. */
        required: boolean;
        values: Record<string, string>;
        errors: Record<string, string>;
        state: 'idle' | 'sending' | 'network';
    };
    composer: {
        text: string;
        uploads: SiteChatUpload[];
        error: string | null;
    };
    actions: SiteChatActions;
};
